import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { LogOut, MapPin, Clock, PawPrint, KeyRound, Play, CheckCircle2, Coffee, Camera, AlertTriangle, PackagePlus, ChevronLeft } from "lucide-react";

export default function CleanerPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState({ today: [], upcoming: [] });
  const [active, setActive] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [keys, setKeys] = useState([]);

  const load = () => api.get("/cleaner/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  const loadOnboarding = () => api.get("/cleaner/onboarding").then(({ data }) => setOnboarding(data)).catch(() => {});
  const loadKeys = () => api.get("/cleaner/keys").then(({ data }) => setKeys(data)).catch(() => {});
  useEffect(() => { load(); loadOnboarding(); loadKeys(); }, []);

  const toggleStep = async (step, value) => {
    try { await api.post("/cleaner/onboarding/step", { step, value }); loadOnboarding(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("category", "onboarding");
    try { await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Document uploaded"); loadOnboarding(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const keyAction = async (kid, act) => {
    try { await api.post(`/cleaner/keys/${kid}/${act}`); toast.success(`Key ${act}`); loadKeys(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const updateBank = async () => {
    const sort = window.prompt("Sort code (e.g. 12-34-56):");
    if (!sort) return;
    const acct = window.prompt("Account number:");
    if (!acct) return;
    let challengeId;
    try {
      const { data } = await api.post("/mfa/request", { purpose: "bank_change" });
      challengeId = data.challenge_id;
      toast.success("We've emailed you a 6-digit verification code");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); return; }
    const code = window.prompt("Enter the 6-digit code we emailed you:");
    if (!code) return;
    try {
      const { data: v } = await api.post("/mfa/verify", { challenge_id: challengeId, code });
      const { data } = await api.post("/cleaner/bank", { sort_code: sort, account_number: acct, mfa_token: v.mfa_token });
      toast.success(`Bank details saved securely (ending ${data.account_last4})`);
      loadOnboarding();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#fdfbf7]">
      <header className="sticky top-0 z-30 bg-[#0a0a0a] border-b border-[#333] px-4 h-14 flex items-center justify-between">
        {active ? (
          <button data-testid="cleaner-back" onClick={() => { setActive(null); load(); }} className="flex items-center gap-1 text-[#d4af37]"><ChevronLeft size={20} /> Jobs</button>
        ) : (
          <div className="flex items-center gap-2"><span className="diamond" /><span className="font-display text-lg">Staff Portal</span></div>
        )}
        <button data-testid="cleaner-logout" onClick={() => { logout(); nav("/"); }} className="text-[#a3a3a3] flex items-center gap-1 text-sm"><LogOut size={16} /></button>
      </header>

      {!active && (
        <div className="p-4 max-w-lg mx-auto">
          <h1 className="font-display text-3xl">Hello, {user?.name?.split(" ")[0]}</h1>
          <p className="text-[#a3a3a3] text-sm mt-1">Your assigned jobs. You only see information for your work.</p>

          {onboarding && !onboarding.ready && (
            <div className="mt-5 bg-[#141414] border border-[#d4af37]/40 rounded-lg p-4" data-testid="onboarding-panel">
              <p className="text-[11px] tracking-luxe uppercase text-[#d4af37] mb-2">Onboarding — {onboarding.onboarding_stage}</p>
              <p className="text-xs text-[#a3a3a3] mb-3">Complete all steps to be eligible for job allocation.</p>
              <div className="space-y-2">
                {onboarding.steps.map((s) => (
                  <button key={s.key} data-testid={`onboarding-step-${s.key}`} onClick={() => toggleStep(s.key, !s.done)}
                    className="w-full flex items-center gap-3 bg-[#0f0f0f] border border-[#333] rounded-lg p-2.5 text-left text-sm">
                    <span className={`w-5 h-5 rounded-sm border flex items-center justify-center ${s.done ? "bg-[#d4af37] border-[#d4af37]" : "border-[#555]"}`}>{s.done && <CheckCircle2 size={14} className="text-[#0a0a0a]" />}</span>
                    {s.label}
                  </button>
                ))}
              </div>
              <label data-testid="onboarding-upload" className="mt-3 inline-flex items-center gap-2 text-sm text-[#d4af37] cursor-pointer"><Camera size={16} /> Upload document (DBS / ID / Right to Work)<input type="file" className="hidden" onChange={uploadDoc} /></label>
              {onboarding.documents?.length > 0 && <p className="text-xs text-[#a3a3a3] mt-2">{onboarding.documents.length} document(s) on file</p>}
            </div>
          )}
          {onboarding && onboarding.ready && (
            <div className="mt-4 text-xs text-green-500 flex items-center gap-2" data-testid="onboarding-ready"><CheckCircle2 size={15} /> Onboarding complete — eligible for jobs</div>
          )}

          <div className="mt-4">
            <button data-testid="cleaner-bank-btn" onClick={updateBank} className="w-full bg-[#141414] border border-[#333] rounded-lg py-3 flex items-center justify-center gap-2 text-sm text-[#d4af37]">
              <KeyRound size={16} /> Update bank details (secure — email code)
            </button>
            {onboarding?.bank_account_last4 && <p className="text-xs text-[#a3a3a3] mt-1 text-center">On file: ••••{onboarding.bank_account_last4}</p>}
          </div>

          {keys.length > 0 && (
            <div className="mt-5 bg-[#141414] border border-[#333] rounded-lg p-4" data-testid="cleaner-keys">
              <p className="text-[11px] tracking-luxe uppercase text-[#d4af37] mb-2">Keys issued to you</p>
              {keys.map((k) => (
                <div key={k.id} className="flex justify-between items-center border-b border-[#2a2a2a] py-2 text-sm">
                  <span><KeyRound size={14} className="inline text-[#d4af37] mr-1" /> {k.label || k.property_ref || "Key"} <span className="text-[#777]">· {k.status}</span></span>
                  <span className="flex gap-2">
                    <button data-testid={`key-received-${k.id}`} onClick={() => keyAction(k.id, "received")} className="text-xs text-[#d4af37]">Received</button>
                    <button data-testid={`key-returned-${k.id}`} onClick={() => keyAction(k.id, "returned")} className="text-xs text-[#a3a3a3]">Returned</button>
                  </span>
                </div>
              ))}
            </div>
          )}

          <Section title={`Today · ${jobs.today.length}`} jobs={jobs.today} onOpen={setActive} testid="today" />
          <Section title={`Upcoming · ${jobs.upcoming.length}`} jobs={jobs.upcoming} onOpen={setActive} testid="upcoming" />

          <div className="mt-8 flex gap-3">
            <QuickAction icon={AlertTriangle} label="Report incident" onClick={() => reportIncident()} testid="quick-incident" />
            <QuickAction icon={PackagePlus} label="Request supplies" onClick={() => requestSupply()} testid="quick-supply" />
          </div>
        </div>
      )}

      {active && <JobDetail job={active} reload={(j) => setActive(j)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );

  async function reportIncident() {
    const description = window.prompt("Describe the incident:");
    if (!description) return;
    try { await api.post("/cleaner/incident", { description, severity: "Standard" }); toast.success("Incident reported"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }
  async function requestSupply() {
    const item = window.prompt("What supplies do you need?");
    if (!item) return;
    try { await api.post("/cleaner/supply-request", { item, quantity: 1 }); toast.success("Supply request sent"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }
}

function Section({ title, jobs, onOpen, testid }) {
  return (
    <div className="mt-6">
      <p className="text-[11px] tracking-luxe uppercase text-[#d4af37] mb-3">{title}</p>
      {jobs.length === 0 ? <p className="text-[#666] text-sm">No jobs.</p> : (
        <div className="space-y-3">
          {jobs.map((j) => (
            <button key={j.id} data-testid={`job-${testid}-${j.id}`} onClick={() => onOpen(j)} className="w-full text-left bg-[#141414] border border-[#333] rounded-lg p-4 hover:border-[#d4af37] transition-colors">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold">{j.service}</p>
                  <p className="text-[#a3a3a3] text-sm mt-0.5">{j.customer_name}</p>
                </div>
                <span className="text-[10px] px-2 py-1 rounded-sm bg-[#d4af37]/15 text-[#d4af37]">{j.status}</span>
              </div>
              <div className="flex gap-4 mt-3 text-xs text-[#a3a3a3]">
                <span className="flex items-center gap-1"><Clock size={13} /> {j.arrival_window || "TBC"}</span>
                <span className="flex items-center gap-1"><MapPin size={13} /> {j.postcode || "—"}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex-1 bg-[#141414] border border-[#333] rounded-lg p-4 flex flex-col items-center gap-2 text-sm">
      <Icon size={22} className="text-[#d4af37]" /> {label}
    </button>
  );
}

function JobDetail({ job, reload, onDone }) {
  const [checklist, setChecklist] = useState(job.checklist || []);
  const [brief, setBrief] = useState(null);
  const inProgress = job.job_status === "In Progress";

  useEffect(() => { api.get(`/cleaner/jobs/${job.id}/brief`).then(({ data }) => setBrief(data)).catch(() => {}); }, [job.id]);
  const markUnsafe = async (itemKey) => {
    const material = window.prompt("Material / fabric (e.g. silk, leather):") || "";
    const reason = window.prompt("Why can't this be cleaned safely?") || "";
    if (!reason) return;
    try { await api.post(`/cleaner/jobs/${job.id}/suitability`, { item_key: itemKey, suitable: false, material, reason }); toast.success("Recorded — office notified for assessment"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const pickPhoto = (label) => new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*"; input.capture = "environment";
    input.onchange = async () => {
      const file = input.files?.[0]; if (!file) return resolve(null);
      const fd = new FormData(); fd.append("file", file); fd.append("category", "elite-task");
      try { const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); resolve(data.id); }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); resolve(null); }
    };
    toast.message(`Select the ${label} photo`); input.click();
  });
  const completeTask = async (tid) => {
    toast.message("Before & after photos are required for Elite tasks");
    const before = await pickPhoto("BEFORE"); if (!before) return;
    const after = await pickPhoto("AFTER"); if (!after) return;
    const mins = window.prompt("Minutes spent on this task?", "30");
    try { await api.post(`/owner/tasks/${tid}/complete`, { before_photo_id: before, after_photo_id: after, time_spent_min: parseInt(mins) || null, notes: "", booking_id: job.id }); toast.success("Task recorded with before/after photos"); const { data } = await api.get(`/cleaner/jobs/${job.id}/brief`); setBrief(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const refresh = async () => { const { data } = await api.get("/cleaner/jobs"); const found = [...data.today, ...data.upcoming].find((x) => x.id === job.id); if (found) { setChecklist(found.checklist || []); reload(found); } };
  const enroute = async () => { try { await api.post(`/cleaner/jobs/${job.id}/enroute`); toast.success("Customer notified you're on the way"); refresh(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const arrived = async () => { try { await api.post(`/cleaner/jobs/${job.id}/arrived`, {}); toast.success("Arrival recorded"); refresh(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const noAccess = async () => {
    const waited = window.prompt("NO ACCESS — how many minutes did you wait?", "15");
    if (waited === null) return;
    const contacted = window.confirm("Did you attempt to contact the customer? OK = Yes, Cancel = No");
    try { const { data } = await api.post(`/cleaner/jobs/${job.id}/no-access`, { waited_minutes: parseInt(waited) || 0, contacted, notes: "" }); toast.success(data.message); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const start = async () => {
    try { await api.post(`/cleaner/jobs/${job.id}/start`); toast.success("Job started"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = async (id) => {
    const next = checklist.map((t) => (t.id === id ? { ...t, done: !t.done } : t));
    setChecklist(next);
    try { await api.put(`/cleaner/jobs/${job.id}/checklist`, { checklist: next }); } catch {}
  };
  const takeBreak = async () => { try { await api.post(`/cleaner/jobs/${job.id}/break`, { type: "break" }); toast.success("Break logged"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const photo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = async () => { try { await api.post(`/cleaner/jobs/${job.id}/photos`, { kind: "after", data: r.result }); toast.success("Photo uploaded"); } catch (err) { toast.error("Upload failed"); } };
    r.readAsDataURL(file);
  };
  const finish = async () => {
    try { const { data } = await api.post(`/cleaner/jobs/${job.id}/finish`, { notes: "" }); toast.success(`Job completed (${data.hours || 0}h logged)`); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-4 max-w-lg mx-auto pb-24">
      <h1 className="font-display text-3xl">{job.service}</h1>
      <p className="text-[#a3a3a3]">{job.reference} · {job.customer_name}</p>

      <div className="bg-[#141414] border border-[#333] rounded-lg p-4 mt-4 space-y-2 text-sm">
        <p className="flex items-center gap-2"><MapPin size={15} className="text-[#d4af37]" /> {job.address || job.postcode || "Address provided on the day"}</p>
        <p className="flex items-center gap-2"><Clock size={15} className="text-[#d4af37]" /> {job.date} · {job.arrival_window}</p>
        {job.access_info && <p className="flex items-center gap-2"><KeyRound size={15} className="text-[#d4af37]" /> {job.access_info}</p>}
        {job.pets && <p className="flex items-center gap-2"><PawPrint size={15} className="text-[#d4af37]" /> {job.pets}</p>}
        {job.notes && <p className="text-[#a3a3a3]">Notes: {job.notes}</p>}
      </div>

      {job.specialist_items?.length > 0 && (
        <div className="bg-[#141414] border border-[#d4af37]/40 rounded-lg p-4 mt-4" data-testid="specialist-job-items">
          <p className="text-[11px] tracking-luxe uppercase text-[#d4af37] mb-2">Specialist services purchased</p>
          <div className="space-y-2">
            {job.specialist_items.map((it, i) => (
              <div key={i} className="flex justify-between items-center text-sm border-b border-[#2a2a2a] pb-2">
                <span className="text-[#cfcabf]">{it.name}{it.quantity > 1 ? ` ×${it.quantity}` : ""}{it.suitability_required && <span className="text-[10px] text-[#d4af37] ml-1">assess</span>}</span>
                <button data-testid={`spec-unsafe-${it.key}`} onClick={() => markUnsafe(it.key)} className="text-xs text-red-400">Can't clean safely</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {brief?.is_owner_elite && (
        <div className="bg-[#141414] border border-[#d4af37]/40 rounded-lg p-4 mt-4" data-testid="owner-brief">
          <p className="text-[11px] tracking-luxe uppercase text-[#d4af37] mb-2">Elite Signature — bring this equipment</p>
          <ul className="text-sm space-y-1 mb-3">{brief.equipment.map((e, i) => <li key={i} className="text-[#cfcabf]">• {e}</li>)}</ul>
          {brief.priorities.length > 0 && <>
            <p className="text-[11px] tracking-luxe uppercase text-[#d4af37] mb-1">Owner priorities</p>
            <ul className="text-sm space-y-1 mb-3">{brief.priorities.map((p) => <li key={p.id} className="text-[#cfcabf]"><span className="text-[#d4af37] text-[10px] mr-1">{p.level}</span>{p.text}</li>)}</ul>
          </>}
          <p className="text-[11px] tracking-luxe uppercase text-[#d4af37] mb-1">Rotating tasks due</p>
          <div className="space-y-1.5">{brief.tasks_due.map((t) => (
            <div key={t.id} className="flex justify-between items-center text-sm">
              <span className="text-[#cfcabf]">{t.label}</span>
              <button data-testid={`owner-task-done-${t.key}`} onClick={() => completeTask(t.id)} className="text-xs text-[#d4af37]">Mark done</button>
            </div>
          ))}{brief.tasks_due.length === 0 && <p className="text-[#666] text-sm">None due today.</p>}</div>
        </div>
      )}

      {!inProgress && job.job_status !== "Completed" && !job.no_access && (
        <>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button data-testid="job-enroute" onClick={enroute} className={`border rounded-lg py-3 flex items-center justify-center gap-2 text-sm ${job.en_route_at ? "border-[#d4af37] text-[#d4af37]" : "border-[#333]"}`}><MapPin size={16} /> {job.en_route_at ? "En route ✓" : "On my way"}</button>
            <button data-testid="job-arrived" onClick={arrived} className={`border rounded-lg py-3 flex items-center justify-center gap-2 text-sm ${job.arrived_at ? "border-[#d4af37] text-[#d4af37]" : "border-[#333]"}`}><Clock size={16} /> {job.arrived_at ? "Arrived ✓" : "Arrived"}</button>
          </div>
          <button data-testid="job-start" onClick={start} className="btn-gold w-full py-4 rounded-lg mt-3 flex items-center justify-center gap-2 text-base"><Play size={18} /> Start job</button>
          <button data-testid="job-no-access" onClick={noAccess} className="w-full py-3 rounded-lg mt-3 border border-red-500/50 text-red-400 flex items-center justify-center gap-2 text-sm"><AlertTriangle size={16} /> Can't get access</button>
        </>
      )}
      {job.no_access && <p className="mt-6 text-center text-red-400 flex items-center justify-center gap-2"><AlertTriangle size={18} /> No access reported — office notified</p>}

      {inProgress && (
        <>
          <div className="mt-5">
            <p className="text-[11px] tracking-luxe uppercase text-[#d4af37] mb-3">Checklist</p>
            <div className="space-y-2">
              {checklist.map((t) => (
                <button key={t.id} data-testid={`check-${t.id}`} onClick={() => toggle(t.id)} className="w-full flex items-center gap-3 bg-[#141414] border border-[#333] rounded-lg p-3 text-left">
                  <span className={`w-5 h-5 rounded-sm border flex items-center justify-center ${t.done ? "bg-[#d4af37] border-[#d4af37]" : "border-[#555]"}`}>{t.done && <CheckCircle2 size={14} className="text-[#0a0a0a]" />}</span>
                  <span className={t.done ? "line-through text-[#666]" : ""}><span className="text-[10px] text-[#d4af37] mr-2">{t.category}</span>{t.task}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <button data-testid="job-break" onClick={takeBreak} className="bg-[#141414] border border-[#333] rounded-lg py-3 flex items-center justify-center gap-2 text-sm"><Coffee size={16} /> Break</button>
            <label data-testid="job-photo" className="bg-[#141414] border border-[#333] rounded-lg py-3 flex items-center justify-center gap-2 text-sm cursor-pointer"><Camera size={16} /> Photo<input type="file" accept="image/*" className="hidden" onChange={photo} /></label>
          </div>
          <button data-testid="job-finish" onClick={finish} className="btn-gold w-full py-4 rounded-lg mt-4 flex items-center justify-center gap-2 text-base"><CheckCircle2 size={18} /> Finish job</button>
        </>
      )}

      {job.job_status === "Completed" && <p className="mt-6 text-center text-green-500 flex items-center justify-center gap-2"><CheckCircle2 size={18} /> Completed — thank you</p>}
    </div>
  );
}
